import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BLOG_POSTS } from '@/data/blogData';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Search, Clock, Calendar, User, ArrowRight, Rss, BookOpen } from 'lucide-react';
import { 
  createCollectionPageSchema, 
  createBreadcrumbListSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';

export default function BlogIndex() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Printing Tips', 'Binding & Finishing', 'Printing Guides'];

  const filteredPosts = BLOG_POSTS.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const collectionSchema = createCollectionPageSchema({
    name: "Inchpaper Print Blog - Printing Guides & Tips",
    description: "Learn expert document printing tips, paper GSM guides, binding comparisons, and PDF formatting secrets.",
    url: "https://print.inchpaper.com/blog",
    hasPart: BLOG_POSTS.map(post => ({ title: post.title, url: `/blog/${post.slug}` }))
  });

  const breadcrumbSchema = createBreadcrumbListSchema([
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" }
  ]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead
        title="Inchpaper Print Blog | Document Printing Guides, Paper GSM & Tips"
        description="Read expert articles on paper GSM selection, spiral vs comb binding comparisons, PDF print formatting secrets, and college project printing tips."
        keywords="printing blog, paper gsm guide, spiral binding vs comb binding, pdf print tips, college printing guide"
        canonical="https://print.inchpaper.com/blog"
        schemas={[
          collectionSchema, 
          breadcrumbSchema,
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Printing & Publishing Guides Blog",
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Header */}
        <div className="bg-gradient-to-r from-red-900 to-slate-900 text-white p-8 sm:p-12 rounded-3xl shadow-lg mb-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Official Print Knowledge Hub</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
              Printing Guides, Paper GSM & Expert Tips
            </h1>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Master the art of document preparation, choose the right binding style, and get maximum value out of every printed page.
            </p>

            {/* Search Box */}
            <div className="pt-2 flex items-center gap-3">
              <div className="relative grow max-w-lg">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search articles, e.g. GSM, binding, PDF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-800/90 text-white placeholder-slate-400 pl-10 pr-4 py-3 rounded-xl text-sm border border-slate-700 focus:outline-hidden focus:ring-2 focus:ring-red-500"
                />
              </div>

              <a
                href="/rss.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-amber-400 rounded-xl text-xs font-semibold border border-slate-700 transition-colors"
              >
                <Rss className="w-4 h-4" />
                <span>RSS Feed</span>
              </a>
            </div>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                selectedCategory === cat
                  ? 'bg-[#7d0909] text-white shadow-xs'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Blog Post Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredPosts.map((post) => (
            <article 
              key={post.slug}
              className="bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md transition-all overflow-hidden flex flex-col justify-between group"
            >
              <div>
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 bg-slate-900/80 backdrop-blur-md text-white text-[11px] font-bold px-2.5 py-1 rounded-md">
                    {post.category}
                  </span>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {post.readTime}
                    </span>
                  </div>

                  <h2 className="text-lg font-bold text-slate-900 group-hover:text-[#7d0909] transition-colors leading-snug line-clamp-2">
                    <Link to={`/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h2>

                  <p className="text-slate-600 text-xs leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {post.author}
                </span>

                <Link
                  to={`/blog/${post.slug}`}
                  className="text-xs font-bold text-[#7d0909] hover:underline flex items-center gap-1"
                >
                  <span>Read Article</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
